import { AppHttpInterceptor } from './app-http.interceptor';
import { TokenValidationService } from './services/token-validation.service';
import { APP_ID, ErrorHandler, inject, Inject, NgModule, NgZone, PLATFORM_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { CustomDateAdapter } from './services/CustomDateAdapter';
import { BrowserModule, provideClientHydration, withHttpTransferCacheOptions, withIncrementalHydration } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TranslateCompiler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CmsModule } from './modules/cms/cms.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CoreModule } from './modules/core/core.module';
import { CustomTranslateLoader } from "./loaders/custom-translate-loader";
import { GlobalErrorHandlerService } from "./error-handler/global-error-handler.service";
import { TranslationHelperService } from './services';
import { RouteReuseStrategy } from '@angular/router';
import { CustomReuseStrategy } from "./reuse-strategy";
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { environment } from '../environments/environment';
import { ServiceWorkerModule } from '@angular/service-worker';
import { StatsigModule, STATSIG_INIT_CONFIG } from '@statsig/angular-bindings';
import { UtilityService } from './services/utility.service';
import { buildStatsigInitConfig } from './services/statsig.service';
import { initializeLocalizedRoutes } from './localize/localize.initializer';
import { PartnerAttributionService } from './services/partner-attribution.service';

declare global {
  interface Window { heap: any; }
  const heap: any;
}

const translationFactory = (httpClient: HttpClient, translationHelperService: TranslationHelperService) => {
  return new CustomTranslateLoader(httpClient, translationHelperService);
};

const getGoogleAuth = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return environment.gtm[window.document.location.hostname.split('.').pop()]?.GTM_auth;
};
const getGoogleId = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return environment.gtm[window.document.location.hostname.split('.').pop()]?.GTM_id;
};
const getGooglePreview = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return environment.gtm[window.document.location.hostname.split('.').pop()]?.GTM_preview;
};

const isServiceWorkerEnabledForHost = () => {
  if (environment.production) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }
  const keywords = ['staging', 'feature'];
  return keywords.some(keyword => window.location.host.includes(keyword));
};

const MY_FORMATS = {
  parse: { dateInput: 'DD-MM-YYYY' },
  display: { dateInput: 'DD-MM-YY', monthYearLabel: 'MMM YYYY' },
};

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CoreModule,
    CmsModule,
    GoogleTagManagerModule.forRoot(
      {
        id: getGoogleId(),
        gtm_auth: getGoogleAuth(),
        gtm_preview: getGooglePreview()
      })
    ,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translationFactory,
        deps: [/* PLATFORM_ID, */HttpClient, TranslationHelperService]
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler
      }
    }),

    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: isServiceWorkerEnabledForHost(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    StatsigModule
  ],
  declarations: [
    AppComponent
  ],
  providers: [
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    provideZoneChangeDetection(),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi()
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppHttpInterceptor,
      multi: true
    },
    {
      provide: APP_ID,
      useValue: 'bah-customer',
    },
    TokenValidationService,
    provideAppInitializer(() => inject(PartnerAttributionService).captureFromCurrentUrl()),
    provideAppInitializer(() => initializeLocalizedRoutes()),
    provideAppInitializer(() => {
      const tokenValidationService = inject(TokenValidationService);
      return tokenValidationService.validate();
    }),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService
    },
    { provide: RouteReuseStrategy, useClass: CustomReuseStrategy },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    provideClientHydration(
      withIncrementalHydration(),
      withHttpTransferCacheOptions({
        filter: (req) => !req.url.includes('/api/country/translation/'),
      }),
    ),
    { provide: STATSIG_INIT_CONFIG, useFactory: buildStatsigInitConfig, deps: [UtilityService, NgZone] },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    @Inject(APP_ID) appId: string
  ) {
    const platform = isPlatformBrowser(platformId) ? 'in the browser' : 'on the server';
    console.log(`Running ${platform} with appId=${appId}`);
  }
}